import React, { useEffect, useRef, useState, useCallback } from 'react';
import FieldHandler from '../../../../models/interfaces/FieldHandler';
import AWSPortOpeningRequestsService from '../../../../services/AWSPortOpeningRequestsService';
import AWSAccountLookupObject from '../../../../models/AWSAccountLookupObject';
import AWSAccount from '../../../../models/entities/AWSAccount';
import { LoadingIndicator, Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import { useValidation } from '../ValidationContext';

interface AWSAccountLookupProps extends FieldHandler<AWSAccount> {
	onAccountChange: any;
	newAccount?: AWSAccount;
	label?: string;
}

interface CacheEntry {
	results: AWSAccountLookupObject[];
	timestamp: number;
}

const AWSAccountLookup = (props: AWSAccountLookupProps) => {
	const HTTP_NOT_FOUND_ERROR_CODE = 404;
	const awsAccountService = new AWSPortOpeningRequestsService();
	const [fieldLabel] = useState<string>("AWS Account's Name or Id");
	const [error, setError] = useState<string>('');
	const [searching, setSearching] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [awsAccountSearchResults, setAWSAccountSearchResults] = useState<AWSAccountLookupObject[]>([]);
	const [retrievedResults, setRetrievedResults] = useState<boolean>(false);
	const searchText = useRef<string>('');

	const debounceTimer = useRef<NodeJS.Timeout | null>(null);
	const abortController = useRef<AbortController | null>(null);
	const cache = useRef<Map<string, CacheEntry>>(new Map());
	const DEBOUNCE_DELAY = 300; // 300ms debounce
	const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);

	const getCacheKey = useCallback((searchTerm: string): string => {
		return `aws_account_${searchTerm.toLowerCase()}`;
	}, []);

	const getCachedResults = useCallback(
		(cacheKey: string): AWSAccountLookupObject[] | null => {
			const cached = cache.current.get(cacheKey);
			if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
				return cached.results;
			}
			if (cached) {
				cache.current.delete(cacheKey);
			}
			return null;
		},
		[CACHE_DURATION]
	);

	const setCachedResults = useCallback((cacheKey: string, results: AWSAccountLookupObject[]): void => {
		cache.current.set(cacheKey, {
			results,
			timestamp: Date.now(),
		});
	}, []);

	const getInitialState = useCallback(() => {
		searchText.current = '';
		setSearching(false);
		setAWSAccountSearchResults([]);
		setRetrievedResults(false);
		setError('');
	}, []);

	const resetState = useCallback(() => {
		getInitialState();
	}, [getInitialState]);

	const searchApplicationInfo = useCallback(
		async (searchString: string) => {
			const cacheKey = getCacheKey(searchString);

			const cachedResults = getCachedResults(cacheKey);
			if (cachedResults) {
				setAWSAccountSearchResults(cachedResults);
				setRetrievedResults(true);
				setSearching(false);
				return;
			}

			if (abortController.current) {
				abortController.current.abort();
			}

			abortController.current = new AbortController();
			setSearching(true);
			setAWSAccountSearchResults([]);

			try {
				const appInfo = await awsAccountService.awsAccountLookUp(searchString, abortController.current.signal);

				if (!abortController.current.signal.aborted) {
					setCachedResults(cacheKey, appInfo);
					setAWSAccountSearchResults(appInfo);
					setRetrievedResults(true);
				}
			} catch (error) {
				if (!abortController.current?.signal.aborted) {
					if (error.name === 'AbortError' || error.name === 'CanceledError') {
						console.log('AWS account search request cancelled for:', searchString);
					} else {
						console.error('AWS account search failed:', error);
						if (error.status !== HTTP_NOT_FOUND_ERROR_CODE) {
							console.log(
								'An unexpected error occurred while retrieving data from Application Repository.'
							);
						}
						setAWSAccountSearchResults([]);
						setRetrievedResults(true);
					}
				}
			} finally {
				if (!abortController.current?.signal.aborted) {
					setSearching(false);
				}
			}
		},
		[awsAccountService, getCacheKey, getCachedResults, setCachedResults]
	);

	const handleTextChange = useCallback(
		async (e) => {
			const inputValue = e.target.value;
			searchText.current = inputValue;
			setAWSAccountSearchResults([]);
			setRetrievedResults(false);
			setError('');

			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}

			const searchString = inputValue.trim();

			if (searchString?.length <= 2) {
				if (abortController.current) {
					abortController.current.abort();
				}
				setSearching(false);
				return;
			}

			debounceTimer.current = setTimeout(() => {
				searchApplicationInfo(searchString);
			}, DEBOUNCE_DELAY);
		},
		[searchApplicationInfo, getCacheKey, DEBOUNCE_DELAY]
	);

	const handleOnChange = useCallback(
		async (value) => {
			const obj = awsAccountSearchResults.find((o) => o.id === value);
			resetState();
			searchText.current = obj?.name ?? obj?.id;
			setRetrievedResults(true);
			setLoading(true);

			try {
				const awsAccount = await awsAccountService.getAWSAccount(parseInt(obj?.id), false);
				props.onAccountChange(awsAccount);
				setAWSAccountSearchResults([]);
			} catch (error) {
				setError(error.response.data.message);
				searchText.current = '';
				setRetrievedResults(true);
			}
			setLoading(false);
		},
		[awsAccountSearchResults, resetState, awsAccountService, props]
	);

	useEffect(() => {
		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
			if (abortController.current) {
				abortController.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (props.newAccount) {
			searchText.current = props.newAccount.account.name;
			props.onAccountChange(props.newAccount);
		}
	}, [props.newAccount, props.onAccountChange]);

	const renderedResults =
		retrievedResults && !awsAccountSearchResults?.length ? (
			<TypeaheadItem value={'0'}>
				<React.Fragment aria-disabled={true}>
					<i>Object was not found</i>
				</React.Fragment>
			</TypeaheadItem>
		) : (
			awsAccountSearchResults?.map((app) => (
				<TypeaheadItem value={app.id} key={app.id}>
					<React.Fragment>
						<span>{app.name}</span>
					</React.Fragment>
				</TypeaheadItem>
			))
		);

	return (
		<>
			{!props.disabled && (
				<Typeahead
					optionalClass={`em-u-width-100` + (!isValid ? ' em-has-error' : '')}
					clickHandler={handleOnChange}
					label={fieldLabel}
					note={!error ? '' : error}
					onChange={handleTextChange}
					placeholder="Account's Name or Id"
					value={searchText.current}
				>
					{searching ? <LoadingIndicator size="s" /> : renderedResults}
				</Typeahead>
			)}
		</>
	);
};

export default AWSAccountLookup;
